import { FunctionComponent, memo, useContext, useEffect, useMemo, useState } from "react";
import { IProps } from "./phrase-trigger-results-interaction-preview.types";
import Modal from "@edgetier/modal";
import InteractionModalContent from "components-for/interactions/interaction-modal-content";
import InteractionPreviews from "components-for/interactions/interaction-modal/interaction-previews";
import { IPhraseTriggerResult } from "@edgetier/watchtower-types";
import { useModalKeyboardShortcut } from "hooks-for/modal/use-modal-keyboard-shortcut";
import { getSurroundingElements } from "utilities/get-surrounding-elements";
import { InteractionContext } from "components-for/interactions/interaction-context/interaction-context";
import { useInteractionMessage } from "hooks-for/interactions/use-interaction-message";
import { SpinnerUntil } from "@edgetier/components";
import "./phrase-trigger-results-interaction-preview.scss";

/**
 * Display a modal with the interaction details belonging to a phrase results
 * @param props.phraseResult
 * @param props.phraseResults
 * @param props.modalProps
 */
const PhraseTriggerResultInteractionPreview: FunctionComponent<IProps> = ({
    phraseResult,
    phraseResults,
    ...modalProps
}) => {
    const [visiblePhraseResult, setVisiblePhraseResult] = useState<IPhraseTriggerResult>(phraseResult);
    const { areWritingIssuesVisible } = useContext(InteractionContext);
    const [phraseResultsBefore, phraseResultsAfter] = useMemo(() => {
        return getSurroundingElements<IPhraseTriggerResult>(phraseResults.indexOf(visiblePhraseResult), phraseResults);
    }, [visiblePhraseResult, phraseResults]);

    const { data: interactionData, isLoading: isInteractionDataLoading } = useInteractionMessage(
        visiblePhraseResult.interactionMessageId
    );

    useEffect(() => {
        setVisiblePhraseResult(phraseResult);
    }, [phraseResult]);

    useModalKeyboardShortcut<IPhraseTriggerResult>(phraseResults, visiblePhraseResult, setVisiblePhraseResult);

    return (
        <Modal size="super-wide" {...modalProps}>
            <InteractionPreviews<IPhraseTriggerResult>
                previewItems={phraseResultsBefore}
                onPreviewClick={setVisiblePhraseResult}
                position="before"
            >
                {(previousPhraseResult) => (
                    <div className="phrase-trigger-results-preview__text">
                        <div className="phrase-trigger-results-preview__summary">
                            <div className="phrase-trigger-results-preview__summary__sentence">
                                {previousPhraseResult.sentence}
                            </div>
                        </div>
                    </div>
                )}
            </InteractionPreviews>
            <SpinnerUntil<{ interactionId: string }> data={interactionData} isReady={!isInteractionDataLoading}>
                {(data) => (
                    <InteractionModalContent
                        interactionId={data.interactionId}
                        areWritingIssuesVisible={areWritingIssuesVisible}
                        highlightedMessageId={visiblePhraseResult.interactionMessageId}
                    />
                )}
            </SpinnerUntil>
            <InteractionPreviews<IPhraseTriggerResult>
                previewItems={phraseResultsAfter}
                onPreviewClick={setVisiblePhraseResult}
                position="after"
            >
                {(nextPhraseResult) => (
                    <div className="phrase-trigger-results-preview__text">
                        <div className="phrase-trigger-results-preview__summary">
                            <div className="phrase-trigger-results-preview__summary__sentence">
                                {nextPhraseResult.sentence}
                            </div>
                        </div>
                    </div>
                )}
            </InteractionPreviews>
        </Modal>
    );
};

export default memo(PhraseTriggerResultInteractionPreview);
